import { useState } from "react";

import { rowSpanClassMap } from "src/core/constants/cartography";
import { EReputationStatus } from "src/core/constants/eReputation";
import { Status } from "src/core/constants/folders";
import { CustomRiskLevel, getRiskLevelValue } from "src/core/constants/risk";
import { Roles } from "src/core/constants/roles";
import { useCartographyRisks, useFolder } from "src/core/contexts/FolderContext";
import { useRiskOptions } from "src/core/contexts/RiskOptionsContext";
import { MissionStatus } from "src/core/gql/graphql";
import { useFolderRisks } from "src/core/hooks/folder/useFolderRisk";
import { usePermissions } from "src/core/hooks/usePermissions";
import {
  FormattedCartographyEntity,
  FormattedCartographyStakeholder,
} from "src/core/types/cartography";

type Entry = {
  label: string;
  risk?: CustomRiskLevel;
};

export const useFolderCartographieTabEC = () => {
  const [openModulerModal, setOpenModulerModal] = useState<boolean>(false);
  const { missionTypeWithRiskOptions } = useRiskOptions();

  const { mappedStakeholders, folderFile, tabRiskLevels, mappedEntities, isPPH } = useFolder();
  const { legalTypesWithRiskOptions, countryWithRiskOptions, activityWithRiskOptions } =
    useRiskOptions();

  // Pour conditionner l'affichage du bouton "Moduler".
  const folderState = folderFile.folderFile?.workflowStatus?.state?.code;

  const { hasRole } = usePermissions();
  const isValidation =
    folderState === Status.VALIDATION_PENDING ||
    (folderState === Status.VALIDATION_PENDING_HR && !hasRole(Roles.AUDITEUR));

  const folderRisks = useFolderRisks();

  const cartographyRisks = useCartographyRisks();

  const getFieldValue = (code: string) =>
    folderFile.folderFile?.fileCustomerFolder?.customer?.allInputValues?.find(
      (input) => input.inputField.fieldCode === code
    )?.stringValue;

  // Données liées à l'activité
  const activityCode = getFieldValue("activityCode");
  const activityMatch = activityWithRiskOptions.find((a) => a.value === activityCode);

  const activityEntry: Entry = {
    label: activityMatch?.label ?? "",
  };

  // Données liées à la localisation
  const paysImmatriculation = getFieldValue("registrationCountryCode");
  const paysImmatriculationMatch = countryWithRiskOptions.find(
    (c) => c.value === paysImmatriculation
  );

  // Données liées au client
  const typeJuridique = getFieldValue("entityTypeCode");
  const typeJuridiqueMatch = legalTypesWithRiskOptions.find((type) => type.value === typeJuridique);

  let riskLevelClient: CustomRiskLevel = paysImmatriculationMatch?.risk as CustomRiskLevel;
  mappedEntities.forEach((entity) => {
    const riskLevelEntity = entity.riskLevel as CustomRiskLevel;
    if (getRiskLevelValue(riskLevelEntity) > getRiskLevelValue(riskLevelClient)) {
      riskLevelClient = riskLevelEntity;
    }
  });
  if (getRiskLevelValue(tabRiskLevels.questionnaire) > getRiskLevelValue(riskLevelClient)) {
    riskLevelClient = tabRiskLevels.questionnaire as CustomRiskLevel;
  }

  const clientEntry: Entry = {
    label: typeJuridiqueMatch?.label ?? "",
    risk: typeJuridiqueMatch?.risk,
  };

  // Données liées à la société
  const corporation = mappedStakeholders.find((s) => s.isFolderCustomer);
  const corporationEntry: Entry = {
    label: corporation?.socialReason ?? "",
    risk: paysImmatriculationMatch?.risk,
  };

  // Données liées aux parties prenantes du dossier (RL et BE).
  const filteredStakeholders = mappedStakeholders.filter((s) => isPPH || !s.isFolderCustomer);
  const nb = filteredStakeholders.length;
  const rowSpanClassStakeholders = rowSpanClassMap[nb] ?? "row-span-1";

  const stakeholders: FormattedCartographyStakeholder[] = filteredStakeholders.map((s, index) => ({
    key: s.id || `${index}`,
    fullName: [s.firstName, s.lastName].filter(Boolean).join(" "),
    // Le risque de cette ligne est lié uniquement au pays de résidence.
    riskLevel: countryWithRiskOptions.find((c) => c.value === s.residenceCountryCode)
      ?.risk as CustomRiskLevel,
    displayPageLabel: index === 0,
  }));

  /**
   * Données liées aux alertes sur les parties prenantes du dossier (PEP et sanctions).
   * PMO / PPH : on affiche tous les stakeholders (même les corporations : FP-406).
   */
  const nbEntities = mappedEntities.length;
  const rowSpanClassEntities = rowSpanClassMap[nbEntities] ?? "row-span-1";

  const entities: FormattedCartographyEntity[] = mappedEntities.map((entity, index) => {
    type AlertType = "noAlert" | "falsePositive" | "pep" | "sanction";
    const alertType: AlertType[] = [];

    if (entity.pepStatus === EReputationStatus.CONFIRMED) {
      alertType.push("pep" as AlertType);
    }
    if (entity.sanctionStatus === EReputationStatus.CONFIRMED) {
      alertType.push("sanction" as AlertType);
    }

    /**
     * PMO :
     * - Si le stakeholder est une personne physique, alors fullName est la concaténation du prénom et du nom.
     * - Si le stakeholder est une entreprise, alors fullName est le nom de l'entreprise.
     * PPH :
     * - fullName est la concaténation du prénom et du nom.
     */
    const fullName =
      (!isPPH && entity.isPPH) || isPPH
        ? [entity.firstName, entity.lastName].filter(Boolean).join(" ")
        : entity.name ?? "";

    return {
      key: entity.id || `${index}`,
      fullName: fullName,
      riskLevel: entity.riskLevel as CustomRiskLevel,
      displayPageLabel: index === 0,
      alertType: alertType,
    };
  });

  // Données liées aux missions
  const filteredMissions =
    folderFile.folderFile?.fileCustomerFolder?.missions.filter(
      (m) => m.status === MissionStatus.Active
    ) ?? [];
  const missions: Entry[] = filteredMissions.map((mission) => ({
    label:
      missionTypeWithRiskOptions.find(
        (m) =>
          m.value ===
          mission.inputValues.find((i) => i.inputField.fieldCode === "missionTypeCode")?.stringValue
      )?.label ?? "",
    risk: missionTypeWithRiskOptions.find(
      (m) =>
        m.value ===
        mission.inputValues.find((i) => i.inputField.fieldCode === "missionTypeCode")?.stringValue
    )?.risk,
  }));

  return {
    folderRisks,
    activityEntry,
    corporationEntry,
    clientEntry,
    cartographyRisks,
    stakeholders,
    entities,
    rowSpanClassStakeholders,
    rowSpanClassEntities,
    tabRiskLevels,
    isValidation,
    openModulerModal,
    setOpenModulerModal,
    missions,
    isPPH,
  };
};
